// Firmware queue page (Phase 2 Slice B).

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext.js';
import { health, type FirmwareEntry, type FirmwareResponse } from '../../lib/api.js';

function severityLabel(s: FirmwareEntry['severity']): string {
  if (s === 'security') return '🔒 Security';
  if (s === 'feature') return '✨ Feature';
  return '— Unknown';
}

function severityColor(s: FirmwareEntry['severity']): string {
  if (s === 'security') return 'var(--ac-danger, crimson)';
  if (s === 'feature') return 'var(--ac-text)';
  return 'var(--ac-text-muted)';
}

export default function FirmwarePage() {
  const { currentHomeId } = useAuth();
  const [data, setData] = useState<FirmwareResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState<Set<string>>(new Set());

  const reload = useCallback(() => {
    if (!currentHomeId) return;
    setLoading(true);
    health
      .firmware(currentHomeId)
      .then((res) => {
        setData(res);
        setError(null);
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Could not load firmware data.';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [currentHomeId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const onInstall = async (entry: FirmwareEntry) => {
    if (!currentHomeId) return;
    setInstalling((prev) => new Set(prev).add(entry.entityId));
    try {
      await health.installFirmware(currentHomeId, entry.entityId);
      // give the bridge a moment to push the new state
      setTimeout(reload, 1500);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Install failed.';
      setError(msg);
    } finally {
      setInstalling((prev) => {
        const next = new Set(prev);
        next.delete(entry.entityId);
        return next;
      });
    }
  };

  if (!currentHomeId) {
    return <p style={{ color: 'var(--ac-text-muted)' }}>Select a home first.</p>;
  }

  if (loading && !data) {
    return <p style={{ color: 'var(--ac-text-muted)' }}>Loading firmware data…</p>;
  }

  if (error) {
    return (
      <div role="alert" style={{ color: 'var(--ac-danger, crimson)' }}>
        {error}
      </div>
    );
  }

  if (!data || data.firmware.length === 0) {
    return (
      <section aria-labelledby="firmware-empty">
        <h1 id="firmware-empty" className="text-xl font-semibold mb-2">
          Firmware
        </h1>
        <p style={{ color: 'var(--ac-text-muted)' }}>
          No firmware-capable devices reported yet. As your bridge syncs HA <code>update.*</code>{' '}
          entities, devices with installable firmware will appear here.
        </p>
      </section>
    );
  }

  return (
    <section aria-labelledby="firmware-heading">
      <header className="mb-4 flex items-baseline justify-between">
        <h1 id="firmware-heading" className="text-xl font-semibold">
          Firmware
        </h1>
        <p style={{ color: 'var(--ac-text-muted)' }}>
          {data.pendingCount} pending · {data.firmware.length} total
        </p>
      </header>

      {data.securityCount > 0 && (
        <div
          role="alert"
          className="mb-4 p-3 rounded"
          style={{
            background: 'var(--ac-danger-bg, rgba(220, 38, 38, 0.1))',
            color: 'var(--ac-danger, crimson)',
          }}
        >
          🔒 {data.securityCount} security update{data.securityCount === 1 ? '' : 's'} available —
          install promptly.
        </div>
      )}

      <table className="w-full text-sm">
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--ac-border)' }}>
            <th scope="col" className="py-2">
              Device
            </th>
            <th scope="col">Severity</th>
            <th scope="col">Current → Latest</th>
            <th scope="col">Action</th>
          </tr>
        </thead>
        <tbody>
          {data.firmware.map((f) => (
            <tr key={f.entityId} style={{ borderBottom: '1px solid var(--ac-border-faint)' }}>
              <th scope="row" className="py-2" style={{ fontWeight: 500 }}>
                {f.title ?? f.entityId}
                {f.releaseUrl && (
                  <a
                    href={f.releaseUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ marginLeft: 6, color: 'var(--ac-link)' }}
                  >
                    notes ↗
                  </a>
                )}
              </th>
              <td style={{ color: severityColor(f.severity) }}>{severityLabel(f.severity)}</td>
              <td style={{ color: 'var(--ac-text-muted)' }}>
                {f.currentVersion ?? '?'} → {f.latestVersion ?? '?'}
              </td>
              <td>
                {f.updateAvailable ? (
                  <button
                    type="button"
                    onClick={() => onInstall(f)}
                    disabled={installing.has(f.entityId) || f.inProgress}
                  >
                    {f.inProgress
                      ? 'Installing…'
                      : installing.has(f.entityId)
                        ? 'Sending…'
                        : 'Install'}
                  </button>
                ) : (
                  <span style={{ color: 'var(--ac-text-muted)' }}>up to date</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
